import { useMutation } from "@tanstack/react-query"
import Cookies from "js-cookie"
import { createContext, useEffect, useState } from "react"

import useOTPRateLimit from "@hooks/useOTPRateLimit"
import useUser from "@hooks/useUser"

import { GenericError } from "@molecules/Form/GenericForm/GenericForm.types"
import toast from "@molecules/Toast/Toast"

import { ACCOUNT_VERIFICATION_TEMPORARY_CODE_ID_HOLD } from "@constants/storage"

import OTPAlertBanner from "./OTPAlertBanner/OTPAlertBanner"
import {
    CodeRequestError,
    CodeRequestPayload,
    CodeRequestResponse,
    CodeVerificationError,
    CodeVerificationPayload,
    CodeVerificationResponse,
    OTPFormContextValues,
    OTPFormProps,
} from "./OTPForm.types"
import OTPSendCodeButton from "./OTPSendCodeButton/OTPSendCodeButton"
import OTPVerifyCodeButton from "./OTPVerifyCodeButton/OTPVerifyCodeButton"

export const OTPFormContext = createContext<OTPFormContextValues | undefined>(undefined)
OTPFormContext.displayName = "OTPFormContext"

function OTPForm(props: OTPFormProps) {
    const {
        codeRequestEndpoint,
        codeVerificationEndpoint,
        children,
        triggerOTPReset,
        onCodeVerificationSuccessful,
        triggerSendCode,
        checkFor,
        email,
        phone,
        hasRequestedCode,
        defaultShowErrorMessage = false,
    } = props

    const { user } = useUser()

    const [otpInputValue, setOtpInputValue] = useState<string>("")
    const [isCodeInvalid, setIsCodeInvalid] = useState<boolean>(false)
    const [isCodeResendBlocked, setIsCodeResendBlocked] = useState<boolean>(false)
    const [isShowingErrorMessage, setIsShowingErrorMessage] = useState<boolean>(defaultShowErrorMessage ?? false)

    useEffect(() => {
        if (triggerOTPReset) {
            setOtpInputValue("")
        }
    }, [triggerOTPReset])

    const {
        isPending: isRequestingCode,
        error: codeRequestError,
        isSuccess: isCodeRequestSuccessful,
        data: codeRequestData,
        isError: isErrorOnCodeSend,
        mutate: requestCode,
    } = useMutation<CodeRequestResponse, GenericError<CodeRequestError>, CodeRequestPayload>({
        mutationFn: async (mutationPayload: CodeRequestPayload): Promise<CodeRequestResponse> => {
            const response = await fetch(codeRequestEndpoint, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
                body: JSON.stringify(mutationPayload),
            })

            if (!response.ok) {
                await response.json().then((error: CodeRequestError) => {
                    throw new GenericError("Could not request code.", error)
                })
            }

            return (await response?.json()) as Promise<CodeRequestResponse>
        },
    })

    const {
        isPending: isVerifyingCode,
        error: codeVerificationError,
        isSuccess: isCodeVerificationSuccessful,
        isError: isErrorOnCodeVerification,
        mutate: verifyCode,
    } = useMutation<CodeVerificationResponse, GenericError<CodeVerificationError>, CodeVerificationPayload>({
        mutationFn: async (mutationPayload: CodeVerificationPayload): Promise<CodeVerificationResponse> => {
            const response = await fetch(codeVerificationEndpoint, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
                body: JSON.stringify(mutationPayload),
            })

            if (!response.ok) {
                await response.json().then((error: CodeVerificationError) => {
                    throw new GenericError("Could not verify code.", error)
                })
            }

            return (await response?.json()) as Promise<CodeVerificationResponse>
        },
    })

    // Store OTP state with timestamp for rate limiting across dialog close/reopen
    const {
        state: otpState,
        setState: setOtpState,
        isValid,
        getRemainingSeconds,
    } = useOTPRateLimit(ACCOUNT_VERIFICATION_TEMPORARY_CODE_ID_HOLD)

    // Derive codeVerificationId for backward compatibility with verification logic
    const codeVerificationId = otpState?.codeVerificationId ?? null
    const noCodeVerificationId = codeVerificationId === null

    // Calculate contact value for validation
    const contactValue = checkFor === "email" ? email || user?.email || "" : phone || user?.phone || ""

    // Check if we have a valid existing code on mount
    const hasValidExistingCode = isValid(checkFor, contactValue)
    const initialCountdownSeconds = hasValidExistingCode ? getRemainingSeconds() : 30

    useEffect(() => {
        if (isCodeVerificationSuccessful) {
            setOtpState(null)
            onCodeVerificationSuccessful()
        }
    }, [isCodeVerificationSuccessful])

    useEffect(() => {
        if (isCodeRequestSuccessful && codeRequestData) {
            const newCodeId = codeRequestData?.email_id || codeRequestData?.phone_id
            if (newCodeId) {
                setOtpState({
                    codeVerificationId: newCodeId,
                    sentAt: Date.now(),
                    checkFor,
                    contactValue,
                })
            }

            setIsCodeResendBlocked(true)

            toast({
                variant: "default",
                type: "success",
                title: "Code sent",
            })
        } else if (hasRequestedCode) {
            setIsCodeResendBlocked(true)
        }
    }, [isCodeRequestSuccessful, codeRequestData, hasRequestedCode])

    useEffect(() => {
        if (isErrorOnCodeSend && noCodeVerificationId) {
            toast({
                variant: "default",
                type: "error",
                title: "Could not send code",
            })
        }
    }, [isErrorOnCodeSend])

    useEffect(() => {
        if (!triggerSendCode) {
            return
        }

        // Check if we have a valid, non-expired code for this contact
        if (isValid(checkFor, contactValue)) {
            // Reuse existing code - just set blocked state, no toast (code wasn't actually sent)
            setIsCodeResendBlocked(true)
        } else {
            // No valid existing code - request a new one
            handleSendCode()
        }
    }, [triggerSendCode])

    useEffect(() => {
        if (isErrorOnCodeVerification) {
            setIsCodeInvalid(true)
        }
    }, [isErrorOnCodeVerification])

    useEffect(() => {
        if (isCodeInvalid) {
            const ONE_AND_HALF_SECOND_MS = 1500
            setTimeout(() => {
                setOtpInputValue("")
                setIsCodeInvalid(false)
            }, ONE_AND_HALF_SECOND_MS)
        }
    }, [isCodeInvalid])

    useEffect(() => {
        if (
            codeVerificationError?.error?.code ||
            codeRequestError?.error?.email_address ||
            codeRequestError?.error?.phone_number ||
            codeRequestError?.error?.__all__ ||
            codeVerificationError?.error?.__all__
        ) {
            setIsShowingErrorMessage(true)
        }
    }, [codeVerificationError, codeRequestError])

    const handleSendCode = () => {
        if (checkFor === "email") {
            requestCode({
                email_address: email || user?.email,
            })
        } else if (checkFor === "phone") {
            requestCode({
                phone_number: phone || user?.phone,
            })
        }
    }

    const handleCodeVerification = () => {
        if (checkFor === "email") {
            verifyCode({
                code: String(otpInputValue),
                email_id: codeVerificationId ?? undefined,
            })
        } else if (checkFor === "phone") {
            verifyCode({
                code: String(otpInputValue),
                phone_id: codeVerificationId ?? undefined,
            })
        }
    }

    const onOTPInputChange = (newValue: string) => {
        setIsShowingErrorMessage(false)

        if (isCodeInvalid) {
            setIsCodeInvalid(false)
        }

        setOtpInputValue(newValue)
    }

    return (
        <OTPFormContext.Provider
            value={{
                errorMessage: codeVerificationError?.error?.code,
                nonFieldErrorMessage:
                    codeVerificationError?.error?.__all__ ||
                    codeRequestError?.error?.email_address ||
                    codeRequestError?.error?.phone_number ||
                    codeRequestError?.error?.__all__,
                handleSendCode,
                isVerifyingCode,
                isCodeResendBlocked,
                otpInputValue,
                onOTPInputChange,
                isCodeInvalid,
                handleCodeVerification,
                setIsCodeInvalid,
                setOtpInputValue,
                isRequestingCode,
                setIsCodeResendBlocked,
                isShowingErrorMessage,
                initialCountdownSeconds,
            }}
        >
            {children}
        </OTPFormContext.Provider>
    )
}

export default {
    Root: OTPForm,
    AlertBanner: OTPAlertBanner,
    SendCodeButton: OTPSendCodeButton,
    VerifyCodeButton: OTPVerifyCodeButton,
}
